// apps/web/src/features/users/components/UsersTable.tsx
// Spec §14.2 / plan Task 5.2. Thin wrapper over <DataTable<User>>: column
// definitions, active/password-reset badges, and a 3-dot row actions menu.
// Destructive actions are disabled client-side when the row targets the
// current user; the server enforces CANNOT_MODIFY_SELF as a 403 regardless.
//
// dropdown escapes the DataTable's `overflow: auto` clipping context.
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type User, formatPhone, phoneSchema } from '@magimp/shared';
import {
  DataTable,
  IconButton,
  Menu,
  MenuItem,
  StatusBadge,
  type Column,
} from '../../../ui/index.js';

export interface UsersTableProps {
  rows: readonly User[];
  onSelect: (u: User) => void;
  emptyState?: ReactNode;
  currentUserId: string;
  onEdit: (u: User) => void;
  onDeactivate: (u: User) => void;
  onReactivate: (u: User) => void;
  onResetPassword: (u: User) => void;
  /**
   * Map of `departmentId -> department display name`. The caller (typically
   * `UsersListPage`) resolves this via `useDepartmentsList()` and passes it
   * down so this component stays a pure presentation layer (no React Query
   * hooks → tests render without a QueryClientProvider). Pass an empty
   * object when the lookup is still loading; the cell falls back to the
   * empty placeholder.
   */
  departmentsById?: Readonly<Record<string, string>>;
}

const DEPARTMENT_FALLBACK = '—';

export function UsersTable({
  rows,
  onSelect,
  emptyState,
  currentUserId,
  onEdit,
  onDeactivate,
  onReactivate,
  onResetPassword,
  departmentsById,
}: UsersTableProps) {
  const { t } = useTranslation(['users', 'common']);
  const columns: Column<User>[] = [
    { key: 'email', label: 'Email', sortable: true },
    { key: 'name', label: t('users:fields.name'), sortable: true },
    {
      key: 'phone',
      label: t('users:fields.phone'),
      // WHY: phoneSchema is the contract; legacy DB rows may pre-date it,
      // so we guard before formatting and fall back to the raw value.
      render: (u: User) =>
        phoneSchema.safeParse(u.phone).success ? formatPhone(u.phone) : u.phone,
    },
    {
      // WHY: User carries the `departmentId` FK only; the display name comes
      // from `departmentsById` (resolved by the caller via useDepartmentsList).
      // While the lookup is loading or when a row's departmentId is not in
      // the current map (e.g. archived department excluded by an active-only
      // filter), the cell shows DEPARTMENT_FALLBACK rather than throwing.
      key: 'department',
      label: t('users:fields.department'),
      render: (u: User) => departmentsById?.[u.departmentId] ?? DEPARTMENT_FALLBACK,
    },
    {
      key: 'role',
      label: t('users:fields.role'),
      sortable: true,
      // WHY: render shows `u.role.displayName`; sortValue must mirror the
      // visible text or the legacy 'sort by role' affordance was a no-op
      // (object → '[object Object]' under the legacy fallback).
      sortValue: (u: User) => u.role.displayName,
      render: (u: User) => u.role.displayName,
    },
    {
      key: 'active',
      label: t('users:fields.status'),
      render: (u: User) => <StatusBadge status={u.active ? 'active' : 'inactive'} />,
    },
    {
      key: 'passwordResetRequired',
      label: '',
      render: (u: User) =>
        u.passwordResetRequired ? <StatusBadge status="passwordResetRequired" size="sm" /> : null,
    },
    {
      key: 'actions',
      label: '',
      render: (u: User) => (
        <RowActions
          user={u}
          isSelf={u.id === currentUserId}
          onEdit={onEdit}
          onDeactivate={onDeactivate}
          onReactivate={onReactivate}
          onResetPassword={onResetPassword}
        />
      ),
    },
  ];
  return (
    <DataTable<User>
      columns={columns}
      rows={rows}
      onSelect={onSelect}
      emptyState={emptyState}
      getRowKey={(u) => u.id}
    />
  );
}

interface RowActionsProps {
  user: User;
  isSelf: boolean;
  onEdit: (u: User) => void;
  onDeactivate: (u: User) => void;
  onReactivate: (u: User) => void;
  onResetPassword: (u: User) => void;
}

// Local component — 3-dot menu trigger + portal-rendered menu.
// Destructive items remain disabled when the row targets the current user.
function RowActions({
  user,
  isSelf,
  onEdit,
  onDeactivate,
  onReactivate,
  onResetPassword,
}: RowActionsProps) {
  const { t } = useTranslation(['users', 'common']);
  return (
    // Stop row-level click propagation so a trigger click does not also
    // select the row. Pure positional container — no user interaction
    // routed through it.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-block' }}>
      <Menu
        trigger={(triggerProps) => (
          <IconButton {...triggerProps} icon="more-vertical" label={t('users:tabs.actions')} />
        )}
      >
        <MenuItem onClick={() => onEdit(user)}>{t('common:actions.edit')}</MenuItem>
        {user.active ? (
          <MenuItem disabled={isSelf} onClick={() => onDeactivate(user)}>
            {t('common:actions.deactivate')}
          </MenuItem>
        ) : (
          <MenuItem onClick={() => onReactivate(user)}>{t('common:actions.reactivate')}</MenuItem>
        )}
        <MenuItem disabled={isSelf} onClick={() => onResetPassword(user)}>
          {t('common:actions.resetPassword')}
        </MenuItem>
      </Menu>
    </div>
  );
}
